/* eslint-disable @next/next/no-img-element */
import "../styles/globals.css";
import type { AppProps, NextWebVitalsMetric } from "next/app";
import { useRouter } from "next/router";
import { ThemeProvider, theme } from "@reusejs/react";
import { webStore, webPersistor } from "store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ContextProvider, getWatermarkedImgixUrl } from "services";
import Head from "next/head";
import { useEffect, useState } from "react";
import { UserAttributeWrapper, MaintenanceBanner } from "app-v2";
import Script from "next/script";
import dynamic from "next/dynamic";

const ScrollToTopButton = dynamic(
  () => import("app").then((mod) => mod.ScrollToTopButton),
  { ssr: false },
);

export function reportWebVitals(metric: NextWebVitalsMetric) {}

interface PageProps {
  metaContent?: any;
  schema?: any;
}

export default function MyApp({ Component, pageProps }: AppProps<PageProps>) {
  const router = useRouter();
  const [isPrintMode, setIsPrintMode] = useState(false);
  useEffect(() => {
    const handlePrint = () => {
      setIsPrintMode(true);
    };

    const handleAfterPrint = () => {
      setIsPrintMode(false);
    };

    window.addEventListener("beforeprint", handlePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handlePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);
  const { metaContent, schema } = pageProps;
  return (
    <>
      <Head>
        {metaContent && (
          <>
            <meta name="title" content={metaContent.title} />
            <meta name="description" content={metaContent.description} />
            <meta property="og:type" content={metaContent.type} />
            <meta property="og:url" content={metaContent.url} />
            <meta property="og:title" content={metaContent.title} />
            <meta property="og:description" content={metaContent.description} />
            <meta
              property="og:image"
              content={
                metaContent.image
                  ? metaContent.image
                  : getWatermarkedImgixUrl(
                      `${process.env.NEXT_PUBLIC_IMAGIX_URL_STATIC}/6a556639-25c9-42e7-94bb-4fc1508c4190`,
                    )
              }
            />
            <meta property="twitter:card" content={metaContent.cardType} />
            <meta property="twitter:url" content={metaContent.url} />
            <meta property="twitter:title" content={metaContent.title} />
            <meta
              property="twitter:description"
              content={metaContent.description}
            />
            <meta
              property="twitter:image"
              content={
                metaContent.image
                  ? metaContent.image
                  : getWatermarkedImgixUrl(
                      `${process.env.NEXT_PUBLIC_IMAGIX_URL_STATIC}/6a556639-25c9-42e7-94bb-4fc1508c4190`,
                    )
              }
            ></meta>
            <meta name="keywords" content={metaContent.keywords} />
            {metaContent.title && <title>{metaContent.title}</title>}
          </>
        )}
        {schema && (
          <Script
            id="schema-scr"
            strategy="afterInteractive"
            type="application/ld+json"
            dangerouslySetInnerHTML={schema}
          />
        )}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <ContextProvider>
        <ThemeProvider value={theme}>
          <Provider store={webStore}>
            <PersistGate loading={null} persistor={webPersistor}>
              <UserAttributeWrapper>
                <MaintenanceBanner key={router.asPath} />
                <Component {...pageProps} />
                {isPrintMode && (
                  <img
                    src="/AuctionBazaar.png"
                    alt="Auction Bazaar"
                    style={{
                      position: "fixed",
                      top: "40%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: "0.7",
                      rotate: "-45deg",
                      zIndex: 10,
                      padding: "10px",
                      objectFit: "contain",
                      height: "10rem",
                      width: "20rem",
                    }}
                  />
                )}
                <ScrollToTopButton />
              </UserAttributeWrapper>
            </PersistGate>
          </Provider>
        </ThemeProvider>
      </ContextProvider>
    </>
  );
}
